import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ws, api } from '../lib/api';
import { useGroupWebRTC } from '../hooks/useGroupWebRTC';
import type { MeetingStatus } from '@sahaj/types';

export interface MeetingParticipantSessionDto {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  isGuest: boolean;
  guestEmail?: string;
  audioMuted: boolean;
  videoOff: boolean;
  screenSharing: boolean;
  joinedAt: string;
}

export interface MeetingRoomDto {
  meetingId: string;
  workspaceId: string;
  hostId: string;
  title: string;
  status: MeetingStatus;
  participants: MeetingParticipantSessionDto[];
}

export interface MeetingSignalMessage {
  meetingId: string;
  workspaceId: string;
  senderId?: string;
  targetUserId?: string;
  signalType: string;
  payload?: any;
}

function getEffectiveUserId(user: any): string {
  if (user?.id) return user.id;
  const storedGuestId = localStorage.getItem('sahaj_auth_guest_id');
  if (storedGuestId) return storedGuestId;
  const token = localStorage.getItem('sahaj_auth_access_token');
  if (token) {
    try {
      const parts = token.split('.');
      if (parts.length >= 2 && parts[1]) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload.guestId) return payload.guestId;
        if (payload.sub) return payload.sub;
      }
    } catch (_e) {}
  }
  return 'guest';
}

interface MeetingContextType {
  activeMeetingRoom: MeetingRoomDto | null;
  participants: MeetingParticipantSessionDto[];
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  screenStream: MediaStream | null;
  isJoined: boolean;
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  meetingError: string | null;
  currentUserId: string;
  joinMeetingRoom: (workspaceId: string, meetingId: string, guestId?: string) => Promise<void>;
  leaveMeetingRoom: () => void;
  endMeetingRoom: () => Promise<void>;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
  clearMeetingError: () => void;
}

const MeetingContext = createContext<MeetingContextType | undefined>(undefined);

export const MeetingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentUserId, setCurrentUserId] = useState<string>(() => getEffectiveUserId(user));

  useEffect(() => {
    const id = getEffectiveUserId(user);
    if (id !== 'guest') {
      setCurrentUserId(id);
    }
  }, [user]);

  const [activeMeetingRoom, setActiveMeetingRoom] = useState<MeetingRoomDto | null>(null);
  const [participants, setParticipants] = useState<MeetingParticipantSessionDto[]>([]);
  const [isJoined, setIsJoined] = useState(false);
  const [meetingError, setMeetingError] = useState<string | null>(null);
  const [iceServers, setIceServers] = useState<RTCIceServer[]>([]);

  const activeMeetingRoomRef = useRef<MeetingRoomDto | null>(null);
  activeMeetingRoomRef.current = activeMeetingRoom;

  const userRef = useRef(user);
  userRef.current = user;

  const effectiveUserIdRef = useRef(currentUserId);
  effectiveUserIdRef.current = currentUserId;

  const handleSignalOut = useCallback((targetUserId: string, signalType: 'OFFER' | 'ANSWER' | 'ICE_CANDIDATE', payload: any) => {
    if (!activeMeetingRoomRef.current) return;
    ws.send('/app/meeting.signal', {
      meetingId: activeMeetingRoomRef.current.meetingId,
      workspaceId: activeMeetingRoomRef.current.workspaceId,
      targetUserId,
      signalType,
      payload,
    });
  }, []);

  const {
    localStream,
    remoteStreams,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    initLocalMedia,
    connectToPeer,
    handleRemoteSignal,
    toggleMute: toggleMuteHook,
    toggleCamera: toggleCameraHook,
    toggleScreenShare: toggleScreenShareHook,
    cleanupPeerConnection,
    cleanupAll: cleanupWebRTC,
  } = useGroupWebRTC({
    myUserId: currentUserId,
    onSignal: handleSignalOut,
    iceServers,
  });

  const connectToPeerRef = useRef(connectToPeer);
  connectToPeerRef.current = connectToPeer;

  const handleRemoteSignalRef = useRef(handleRemoteSignal);
  handleRemoteSignalRef.current = handleRemoteSignal;

  const cleanupWebRTCRef = useRef(cleanupWebRTC);
  cleanupWebRTCRef.current = cleanupWebRTC;

  const leaveMeetingRoom = useCallback(() => {
    if (activeMeetingRoomRef.current) {
      ws.send('/app/meeting.leave', {
        meetingId: activeMeetingRoomRef.current.meetingId,
        workspaceId: activeMeetingRoomRef.current.workspaceId,
      });
    }
    cleanupWebRTCRef.current();
    setActiveMeetingRoom(null);
    setParticipants([]);
    setIsJoined(false);
  }, []);

  const endMeetingRoom = useCallback(async () => {
    if (activeMeetingRoomRef.current) {
      const mId = activeMeetingRoomRef.current.meetingId;
      const wId = activeMeetingRoomRef.current.workspaceId;
      leaveMeetingRoom();
      try {
        await api.meetings.end(wId, mId);
      } catch (_e) {}
    }
  }, [leaveMeetingRoom]);

  const joinMeetingRoom = useCallback(async (workspaceId: string, meetingId: string, guestId?: string) => {
    setMeetingError(null);
    if (guestId) {
      setCurrentUserId(guestId);
      localStorage.setItem('sahaj_auth_guest_id', guestId);
    } else {
      const effId = getEffectiveUserId(userRef.current);
      if (effId !== 'guest') {
        setCurrentUserId(effId);
      }
    }

    try {
      const servers = await api.groupCalls.getIceServers(workspaceId);
      if (servers && Array.isArray(servers)) {
        setIceServers(servers);
      }
    } catch (_e) {}

    try {
      await initLocalMedia('VIDEO');
    } catch (err) {
      console.error('[Meeting] Media access error:', err);
    }

    ws.send('/app/meeting.join', {
      workspaceId,
      meetingId,
    });
  }, [initLocalMedia]);

  const notifyMediaToggle = useCallback((audioMuted: boolean, videoOff: boolean, screenSharing: boolean) => {
    if (!activeMeetingRoomRef.current) return;
    ws.send('/app/meeting.media-toggle', {
      meetingId: activeMeetingRoomRef.current.meetingId,
      audioMuted,
      videoOff,
      screenSharing,
    });
  }, []);

  const toggleMute = useCallback(() => {
    toggleMuteHook();
    notifyMediaToggle(!isMuted, isCameraOff, isScreenSharing);
  }, [toggleMuteHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const toggleCamera = useCallback(() => {
    toggleCameraHook();
    notifyMediaToggle(isMuted, !isCameraOff, isScreenSharing);
  }, [toggleCameraHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const toggleScreenShare = useCallback(() => {
    toggleScreenShareHook();
    notifyMediaToggle(isMuted, isCameraOff, !isScreenSharing);
  }, [toggleScreenShareHook, notifyMediaToggle, isMuted, isCameraOff, isScreenSharing]);

  const clearMeetingError = useCallback(() => setMeetingError(null), []);

  // STOMP User Queue Listener (/user/queue/calls)
  useEffect(() => {
    const unsubscribeQueue = ws.subscribe('/user/queue/calls', async (signal: MeetingSignalMessage) => {
      if (signal.signalType === 'MEETING_ROOM_JOINED' || signal.signalType === 'ROOM_JOINED') {
        const room = signal.payload?.room;
        if (room && room.meetingId) {
          console.log('[Meeting] ROOM_JOINED:', room.meetingId);
          setActiveMeetingRoom(room);
          setParticipants(room.participants || []);
          setIsJoined(true);

          let myId = effectiveUserIdRef.current;
          if ((myId === 'guest' || !myId) && room.participants) {
            const guestPart = room.participants.find((p: any) => p.isGuest) || room.participants[room.participants.length - 1];
            if (guestPart && guestPart.userId) {
              myId = guestPart.userId;
              setCurrentUserId(myId);
              localStorage.setItem('sahaj_auth_guest_id', myId);
            }
          }

          if (localStream && room.participants) {
            for (const p of room.participants) {
              if (p.userId !== myId) {
                await connectToPeerRef.current(p.userId, localStream);
              }
            }
          }
        }
      } else if (signal.signalType === 'MEETING_ROOM_FULL' || signal.signalType === 'ROOM_FULL') {
        setMeetingError('Meeting room is full');
        cleanupWebRTCRef.current();
        setIsJoined(false);
        setActiveMeetingRoom(null);
      } else if (signal.signalType === 'OFFER' || signal.signalType === 'ANSWER' || signal.signalType === 'ICE_CANDIDATE') {
        if (signal.senderId) {
          await handleRemoteSignalRef.current(signal.senderId, signal.signalType, signal.payload);
        }
      }
    });

    return () => {
      unsubscribeQueue();
    };
  }, [localStream]);

  // STOMP Meeting Topic Listener (/topic/meeting.{mId}.call)
  useEffect(() => {
    if (!activeMeetingRoom?.meetingId) return;

    const topic = `/topic/meeting.${activeMeetingRoom.meetingId}.call`;
    const unsubscribeTopic = ws.subscribe(topic, (signal: MeetingSignalMessage) => {
      const myId = effectiveUserIdRef.current;

      if (signal.signalType === 'MEETING_PARTICIPANT_JOINED' || signal.signalType === 'PARTICIPANT_JOINED') {
        const newPart = signal.payload?.participant;
        if (newPart && newPart.userId !== myId) {
          console.log('[Meeting] Participant joined:', newPart.userId);
          setParticipants((prev) => {
            if (prev.some((p) => p.userId === newPart.userId)) return prev;
            return [...prev, newPart];
          });
          if (localStream) {
            connectToPeerRef.current(newPart.userId, localStream);
          }
        }
      } else if (signal.signalType === 'MEETING_PARTICIPANT_LEFT' || signal.signalType === 'PARTICIPANT_LEFT') {
        const leftUserId = signal.payload?.userId || signal.senderId;
        if (leftUserId) {
          console.log('[Meeting] Participant left:', leftUserId);
          setParticipants((prev) => prev.filter((p) => p.userId !== leftUserId));
          cleanupPeerConnection(leftUserId);
        }
      } else if (signal.signalType === 'MEETING_PARTICIPANT_MEDIA_CHANGED' || signal.signalType === 'PARTICIPANT_MEDIA_CHANGED') {
        const senderId = signal.senderId;
        const mediaState = signal.payload?.mediaState;
        if (senderId && mediaState) {
          setParticipants((prev) =>
            prev.map((p) =>
              p.userId === senderId
                ? {
                    ...p,
                    audioMuted: mediaState.audioMuted,
                    videoOff: mediaState.videoOff,
                    screenSharing: mediaState.screenSharing,
                  }
                : p
            )
          );
        }
      } else if (signal.signalType === 'MEETING_ENDED') {
        console.log('[Meeting] Host ended meeting:', activeMeetingRoom?.meetingId);
        cleanupWebRTCRef.current();
        setActiveMeetingRoom(null);
        setParticipants([]);
        setIsJoined(false);
      }
    });

    return () => {
      unsubscribeTopic();
    };
  }, [activeMeetingRoom?.meetingId, localStream, cleanupPeerConnection]);

  return (
    <MeetingContext.Provider
      value={{
        activeMeetingRoom,
        participants,
        localStream,
        remoteStreams,
        screenStream,
        isJoined,
        isMuted,
        isCameraOff,
        isScreenSharing,
        meetingError,
        currentUserId,
        joinMeetingRoom,
        leaveMeetingRoom,
        endMeetingRoom,
        toggleMute,
        toggleCamera,
        toggleScreenShare,
        clearMeetingError,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
};

export const useMeeting = () => {
  const context = useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeeting must be used within a MeetingProvider');
  }
  return context;
};
